import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { OfferTemplateConfig, TemplatePage, TemplateBlock } from '../../../../../types/offerTemplate.types';
import { hexToRgba } from './pdfRenderUtils';
import BlockRendererFactory from './BlockRendererFactory';

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

const HEADER_HEIGHT = 90;
const ACCENT_BAR = 4;
const BODY_PADDING_TOTAL = 40; // gövde dikey padding (20px üst + 20px alt)
const BLOCK_GAP = 14;
const TABLE_HEADER_ROW = 36;
const TABLE_DATA_ROW = 32;
// Görsel/yazı tipi yüklenmesi gibi ölçüm sonrası ufak oynamalara karşı emniyet payı
const PAGE_SAFETY = 14;

// Ölçüm tamamlanmadan önceki ilk render için kaba tahminler
const BEFORE_TABLE_HEIGHT = 200;
const AFTER_TABLE_HEIGHT = 300;

const FIRST_PAGE_ROWS = Math.floor(
  (A4_HEIGHT - HEADER_HEIGHT - ACCENT_BAR - BODY_PADDING_TOTAL - BEFORE_TABLE_HEIGHT - AFTER_TABLE_HEIGHT - TABLE_HEADER_ROW) / TABLE_DATA_ROW
);

const CONTINUATION_ROWS_FULL = Math.floor(
  (A4_HEIGHT - HEADER_HEIGHT - ACCENT_BAR - BODY_PADDING_TOTAL - TABLE_HEADER_ROW) / TABLE_DATA_ROW
);

const LAST_CONTINUATION_ROWS = Math.floor(
  (A4_HEIGHT - HEADER_HEIGHT - ACCENT_BAR - BODY_PADDING_TOTAL - TABLE_HEADER_ROW - AFTER_TABLE_HEIGHT) / TABLE_DATA_ROW
);

/**
 * Gizli ölçüm katmanından okunan gerçek yükseklikler.
 * Satırlar uzun ürün adı/açıklama nedeniyle birden fazla satıra sarabildiği için
 * sabit satır yüksekliği varsayımı yerine DOM'dan ölçülen değerler kullanılır.
 */
interface PageMeasurement {
  headerHeight: number;   // sayfa başlığı + aksan çizgisi
  preHeight: number;      // tablo öncesi blokların toplamı (aralar dahil)
  markerHeight: number;   // "... tablo devamı" ibaresi
  theadHeight: number;    // tablo başlık satırı
  rowHeights: number[];   // her veri satırının gerçek yüksekliği
  postHeights: number[];  // tablo sonrası blokların tek tek yükseklikleri
}

export interface OfferPageRendererProps {
  page: TemplatePage;
  config: OfferTemplateConfig;
  offer?: any;
  offerType?: 'given' | 'received';
  companyInfo?: any;
}

interface PageChunk {
  preTableBlocks: TemplateBlock[];
  tableBlock: TemplateBlock | null;
  itemSlice: any[];
  itemStartIndex: number;
  postTableBlocks: TemplateBlock[];
  isFirstPage: boolean;
  isContinuation: boolean;
}

const OfferPageHeader: React.FC<{ config: OfferTemplateConfig; companyInfo?: any; isGiven: boolean; isContinuation?: boolean }> = ({ config, companyInfo, isGiven, isContinuation }) => (
  <>
    <div style={{
      background: `linear-gradient(135deg, ${config.anaRenk} 0%, ${config.aksanRenk} 100%)`,
      color: '#fff', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      position: 'relative', flexShrink: 0, overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', right: -40, top: -40, width: 180, height: 180, borderRadius: '50%', backgroundColor: hexToRgba(config.aksanRenk, 0.2) }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, zIndex: 1 }}>
        {config.logoUrl ? <img src={config.logoUrl} alt="Logo" style={{ maxHeight: 50, maxWidth: 140, objectFit: 'contain', borderRadius: 4 }} crossOrigin="anonymous" /> : null}
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>{companyInfo?.name || 'Firma Adı'}</div>
          {config.slogan ? <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2, fontStyle: 'italic' }}>{config.slogan}</div> : null}
        </div>
      </div>
      <div style={{ textAlign: 'right', zIndex: 1 }}>
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 3 }}>{isGiven ? 'TEKLİF' : 'ALIŞ TEKLİFİ'}</div>
        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
          {isContinuation ? '(devam)' : (isGiven ? 'Satış Teklifi' : 'Tedarikçi Teklifi')}
        </div>
      </div>
    </div>
    <div style={{ height: 4, background: `linear-gradient(to right, ${config.aksanRenk}, transparent)`, flexShrink: 0 }} />
  </>
);

const singleChunk = (blocks: TemplateBlock[]): PageChunk[] => ([{
  preTableBlocks: blocks,
  tableBlock: null,
  itemSlice: [],
  itemStartIndex: 0,
  postTableBlocks: [],
  isFirstPage: true,
  isContinuation: false,
}]);

const OfferPageRenderer: React.FC<OfferPageRendererProps> = ({ page, config, offer, offerType, companyInfo }) => {
  const blocks = useMemo(() => {
    const list = page.blocks ?? [];
    return [...list].filter(b => b.visible).sort((a, b) => a.order - b.order);
  }, [page.blocks]);

  const isGiven = offerType !== 'received';

  const tableBlockIndex = blocks.findIndex(b => b.type === 'itemsTable');
  const tableBlock = tableBlockIndex >= 0 ? blocks[tableBlockIndex] : null;
  const items: any[] = Array.isArray(offer?.items) ? offer.items : [];
  const canMeasure = Boolean(tableBlock) && items.length > 0;

  const measureRef = useRef<HTMLDivElement | null>(null);
  const lastMeasureKey = useRef<string>('');
  const [measurement, setMeasurement] = useState<PageMeasurement | null>(null);
  const [fontsTick, setFontsTick] = useState(0);

  // Yazı tipleri geç yüklenirse satır yükseklikleri değişebilir; hazır olunca yeniden ölç
  useEffect(() => {
    let alive = true;
    const fonts = (document as any).fonts;
    if (fonts?.ready?.then) {
      fonts.ready.then(() => { if (alive) setFontsTick(1); });
    }
    return () => { alive = false; };
  }, []);

  useLayoutEffect(() => {
    const root = measureRef.current;
    if (!root) {
      if (lastMeasureKey.current !== '') {
        lastMeasureKey.current = '';
        setMeasurement(null);
      }
      return;
    }
    const headerEl = root.querySelector('[data-m="header"]') as HTMLElement | null;
    const preEl = root.querySelector('[data-m="pre"]') as HTMLElement | null;
    const markerEl = root.querySelector('[data-m="table"] [data-m-marker]') as HTMLElement | null;
    const theadEl = root.querySelector('[data-m="table"] thead') as HTMLElement | null;
    const rowEls = Array.from(root.querySelectorAll('[data-m="table"] tbody tr')) as HTMLElement[];
    const postEls = Array.from(root.querySelectorAll('[data-m="post"]')) as HTMLElement[];

    const next: PageMeasurement = {
      headerHeight: headerEl ? headerEl.getBoundingClientRect().height : HEADER_HEIGHT + ACCENT_BAR,
      preHeight: preEl ? preEl.getBoundingClientRect().height : 0,
      markerHeight: markerEl ? markerEl.getBoundingClientRect().height + 6 : 24,
      theadHeight: theadEl ? theadEl.getBoundingClientRect().height : TABLE_HEADER_ROW,
      rowHeights: rowEls.map(el => el.getBoundingClientRect().height),
      postHeights: postEls.map(el => el.getBoundingClientRect().height),
    };
    const key = JSON.stringify(next);
    if (key !== lastMeasureKey.current) {
      lastMeasureKey.current = key;
      setMeasurement(next);
    }
  }, [blocks, items, config, tableBlockIndex, canMeasure, fontsTick]);

  const pageChunks: PageChunk[] = useMemo(() => {
    if (!tableBlock || items.length === 0) {
      return singleChunk(blocks);
    }

    const preTableBlocks = blocks.slice(0, tableBlockIndex);
    const postTableBlocks = blocks.slice(tableBlockIndex + 1);

    // Ölçüm hazırsa gerçek yüksekliklere göre sayfala
    if (measurement && measurement.rowHeights.length === items.length) {
      const m = measurement;
      const pageContent = A4_HEIGHT - m.headerHeight - BODY_PADDING_TOTAL - PAGE_SAFETY;

      const rowsTotal = m.rowHeights.reduce((a, b) => a + b, 0);
      const postTotal = m.postHeights.reduce((a, b) => a + b + BLOCK_GAP, 0);
      const preWithGap = m.preHeight + (preTableBlocks.length > 0 ? BLOCK_GAP : 0);
      if (preWithGap + m.theadHeight + rowsTotal + postTotal <= pageContent) {
        return singleChunk(blocks);
      }

      const chunks: PageChunk[] = [];
      let idx = 0;
      let first = true;
      let usedOnLastPage = 0;

      while (idx < items.length) {
        let used = (first ? preWithGap : m.markerHeight) + m.theadHeight;
        const start = idx;
        while (idx < items.length && (idx === start || used + m.rowHeights[idx] <= pageContent)) {
          used += m.rowHeights[idx];
          idx++;
        }
        chunks.push({
          preTableBlocks: first ? preTableBlocks : [],
          tableBlock,
          itemSlice: items.slice(start, idx),
          itemStartIndex: start,
          postTableBlocks: [],
          isFirstPage: first,
          isContinuation: !first,
        });
        usedOnLastPage = used;
        first = false;
      }

      // Tablo sonrası blokları (toplamlar, notlar, imza...) sığdıkça son sayfaya,
      // sığmayanları yeni sayfalara yerleştir
      let current = chunks[chunks.length - 1];
      let currentUsed = usedOnLastPage;
      postTableBlocks.forEach((block, p) => {
        const blockHeight = (m.postHeights[p] ?? 0) + BLOCK_GAP;
        if (currentUsed + blockHeight <= pageContent) {
          current.postTableBlocks.push(block);
          currentUsed += blockHeight;
        } else {
          const overflowChunk: PageChunk = {
            preTableBlocks: [],
            tableBlock: null,
            itemSlice: [],
            itemStartIndex: 0,
            postTableBlocks: [block],
            isFirstPage: false,
            isContinuation: true,
          };
          chunks.push(overflowChunk);
          current = overflowChunk;
          currentUsed = blockHeight;
        }
      });

      return chunks;
    }

    // Ölçüm henüz yoksa eski tahmine dayalı bölme (ilk render için geçici)
    if (items.length <= FIRST_PAGE_ROWS) {
      return singleChunk(blocks);
    }

    const chunks: PageChunk[] = [];
    let remaining = [...items];
    let globalIdx = 0;

    const firstSlice = remaining.slice(0, FIRST_PAGE_ROWS);
    remaining = remaining.slice(FIRST_PAGE_ROWS);
    chunks.push({
      preTableBlocks,
      tableBlock,
      itemSlice: firstSlice,
      itemStartIndex: globalIdx,
      postTableBlocks: [],
      isFirstPage: true,
      isContinuation: false,
    });
    globalIdx += firstSlice.length;

    while (remaining.length > 0) {
      const isLast = remaining.length <= LAST_CONTINUATION_ROWS;
      const take = isLast ? remaining.length : Math.min(CONTINUATION_ROWS_FULL, remaining.length);
      const slice = remaining.slice(0, take);
      remaining = remaining.slice(take);

      chunks.push({
        preTableBlocks: [],
        tableBlock,
        itemSlice: slice,
        itemStartIndex: globalIdx,
        postTableBlocks: remaining.length === 0 ? postTableBlocks : [],
        isFirstPage: false,
        isContinuation: true,
      });
      globalIdx += slice.length;
    }

    return chunks;
  }, [tableBlock, blocks, items, tableBlockIndex, measurement]);

  const fontStack = `'${config.font}', Arial, sans-serif`;

  return (
    <>
      {canMeasure && (
        <div
          ref={measureRef}
          aria-hidden
          style={{
            position: 'absolute',
            left: -100000,
            top: 0,
            width: A4_WIDTH,
            visibility: 'hidden',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            backgroundColor: '#fff',
            fontFamily: fontStack,
            fontSize: config.fontBoyutu,
            color: '#333',
          }}
        >
          <div data-m="header">
            <OfferPageHeader config={config} companyInfo={companyInfo} isGiven={isGiven} />
          </div>
          <div style={{ padding: '20px 40px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: BLOCK_GAP }}>
              <div data-m="pre">
                <div style={{ display: 'flex', flexDirection: 'column', gap: BLOCK_GAP }}>
                  {blocks.slice(0, tableBlockIndex).map(block => (
                    <BlockRendererFactory key={`m-${block.id}`} block={block} config={config} offer={offer} offerType={offerType} companyInfo={companyInfo} />
                  ))}
                </div>
              </div>
              <div data-m="table">
                <ItemsTableSlice
                  block={tableBlock as TemplateBlock}
                  config={config}
                  items={items}
                  startIndex={0}
                  currency={offer?.currency || 'TRY'}
                  isContinuation
                />
              </div>
              {blocks.slice(tableBlockIndex + 1).map(block => (
                <div data-m="post" key={`m-${block.id}`}>
                  <BlockRendererFactory block={block} config={config} offer={offer} offerType={offerType} companyInfo={companyInfo} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {pageChunks.map((chunk, chunkIdx) => (
        <div
          key={`${page.id}-chunk-${chunkIdx}`}
          data-pdf-page="true"
          style={{
            width: A4_WIDTH,
            minHeight: A4_HEIGHT,
            boxSizing: 'border-box',
            backgroundColor: '#fff',
            fontFamily: fontStack,
            fontSize: config.fontBoyutu,
            color: '#333',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <OfferPageHeader config={config} companyInfo={companyInfo} isGiven={isGiven} isContinuation={chunk.isContinuation} />

          <div style={{ padding: '20px 40px', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: BLOCK_GAP }}>
              {chunk.preTableBlocks.map(block => (
                <BlockRendererFactory key={block.id} block={block} config={config} offer={offer} offerType={offerType} companyInfo={companyInfo} />
              ))}

              {chunk.tableBlock && chunk.itemSlice.length > 0 && (
                <ItemsTableSlice
                  block={chunk.tableBlock}
                  config={config}
                  items={chunk.itemSlice}
                  startIndex={chunk.itemStartIndex}
                  currency={offer?.currency || 'TRY'}
                  isContinuation={chunk.isContinuation}
                />
              )}

              {chunk.postTableBlocks.map(block => (
                <BlockRendererFactory key={block.id} block={block} config={config} offer={offer} offerType={offerType} companyInfo={companyInfo} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

const formatCurrency = (amount: number | undefined): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const hexToRgbaLocal = (hex: string, alpha: number): string => {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch { return `rgba(0,0,0,${alpha})`; }
};

interface ItemsTableSliceProps {
  block: TemplateBlock;
  config: OfferTemplateConfig;
  items: any[];
  startIndex: number;
  currency: string;
  isContinuation: boolean;
}

const ItemsTableSlice: React.FC<ItemsTableSliceProps> = ({ block, config, items, startIndex, currency, isContinuation }) => {
  const s = block.settings ?? {};
  const zebraStripes = s.zebraStripes !== false;
  const headerBgColor = typeof s.headerBgColor === 'string' && s.headerBgColor ? s.headerBgColor : config.anaRenk;
  const showDiscountSetting = s.showDiscount === true;
  const showSctSetting = s.showSct === true;

  const anyDiscount = items.some((it: any) => typeof it?.discountRate === 'number' && it.discountRate > 0);
  const anySct = items.some((it: any) => typeof it?.sctRate === 'number' && it.sctRate > 0);
  const showDiscountCol = showDiscountSetting && anyDiscount;
  const showSctCol = showSctSetting && anySct;

  const fs = `${Math.max(config.fontBoyutu - 1, 10)}px`;
  const thStyle: React.CSSProperties = { backgroundColor: headerBgColor, color: '#fff', padding: '8px 6px', fontSize: fs, fontWeight: 600, textAlign: 'left', borderBottom: `2px solid ${config.aksanRenk}` };
  const tdStyle: React.CSSProperties = { padding: '7px 6px', fontSize: fs, borderBottom: '1px solid #eee', verticalAlign: 'top' };

  let zebraBg = '#f9f9f9';
  try { zebraBg = hexToRgbaLocal(config.aksanRenk, 0.04); } catch {}

  return (
    <div style={{ width: '100%' }}>
      {isContinuation && (
        <div data-m-marker="true" style={{ fontSize: `${config.fontBoyutu - 2}px`, color: '#888', marginBottom: 6, fontStyle: 'italic' }}>
          ... tablo devamı
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: `'${config.font}', Arial, sans-serif` }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 32 }}>#</th>
            <th style={thStyle}>Mal/Hizmet</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
            <th style={thStyle}>Birim</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Birim Fiyat</th>
            {showDiscountCol ? <th style={{ ...thStyle, textAlign: 'right' }}>İsk.%</th> : null}
            <th style={{ ...thStyle, textAlign: 'right' }}>KDV%</th>
            {showSctCol ? <th style={{ ...thStyle, textAlign: 'right' }}>ÖTV%</th> : null}
            <th style={{ ...thStyle, textAlign: 'right' }}>Tutar</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it: any, idx: number) => {
            const globalIdx = startIndex + idx;
            const bg = zebraStripes && globalIdx % 2 === 1 ? zebraBg : '#fff';
            const name = it?.name ?? it?.itemName ?? '-';
            const qty = typeof it?.quantity === 'number' ? it.quantity : '-';
            const unit = it?.unit ? String(it.unit) : '-';
            const unitPrice = typeof it?.unitPrice === 'number' ? it.unitPrice : undefined;
            const vat = typeof it?.vatRate === 'number' ? it.vatRate : '-';
            const disc = typeof it?.discountRate === 'number' ? it.discountRate : '-';
            const sct = typeof it?.sctRate === 'number' ? it.sctRate : '-';
            const total = it?.totalPrice ?? it?.grossTotal ?? undefined;
            return (
              <tr key={it?.id ?? `r-${globalIdx}`} style={{ backgroundColor: bg }}>
                <td style={tdStyle}>{globalIdx + 1}</td>
                <td style={tdStyle}>{String(name)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{qty}</td>
                <td style={tdStyle}>{unit}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(unitPrice)}</td>
                {showDiscountCol ? <td style={{ ...tdStyle, textAlign: 'right' }}>{disc === '-' ? '-' : `${disc}`}</td> : null}
                <td style={{ ...tdStyle, textAlign: 'right' }}>{vat === '-' ? '-' : `${vat}`}</td>
                {showSctCol ? <td style={{ ...tdStyle, textAlign: 'right' }}>{sct === '-' ? '-' : `${sct}`}</td> : null}
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OfferPageRenderer;
